import { useQuery } from '@tanstack/react-query';
import { Bell, LogOut, Search, User as UserIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { LanguageSwitcher } from '@/components/language-switcher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useAlertasRealtime } from '@/lib/use-alertas-realtime';

export function Topbar() {
  const { user, role, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Realtime de alertas operacionais — silencia toast quando já está na pagina
  useAlertasRealtime({ silenciarToast: location.pathname.startsWith('/alertas') });

  // Contagem de alertas em aberto para o badge
  const alertasAbertos = useQuery({
    queryKey: ['alertas-abertos'],
    queryFn: async () => {
      const { count } = await supabase
        .from('tab_alertas_operacionais')
        .select('seq_alerta_operacional', { count: 'exact', head: true })
        .eq('cod_status', 'ABERTO');
      return count ?? 0;
    },
    enabled: !!user,
    refetchInterval: 60_000,
  });

  async function handleLogout() {
    await signOut();
    toast.success('Sessao encerrada');
    navigate('/login');
  }

  return (
    <header className="bg-background flex h-16 items-center gap-4 border-b px-6">
      <div className="relative max-w-md flex-1">
        <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          placeholder="Buscar..."
          className="pl-9"
          onKeyDown={(e) => {
            if (e.key === '/') e.preventDefault();
          }}
        />
      </div>

      <div className="flex items-center gap-3">
        <LanguageSwitcher compact />
        <Link
          to="/alertas"
          className="text-muted-foreground hover:text-foreground relative"
          aria-label={`Alertas operacionais${
            alertasAbertos.data ? ` (${alertasAbertos.data} em aberto)` : ''
          }`}
        >
          <Bell className="h-5 w-5" />
          {alertasAbertos.data && alertasAbertos.data > 0 ? (
            <span className="bg-destructive text-destructive-foreground absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold">
              {alertasAbertos.data > 9 ? '9+' : alertasAbertos.data}
            </span>
          ) : null}
        </Link>
        <div className="hidden text-right text-xs leading-tight md:block">
          <div className="font-medium">{user?.email ?? '—'}</div>
          <div className="text-muted-foreground capitalize">{role ?? 'sem papel'}</div>
        </div>
        <div className="bg-muted flex h-9 w-9 items-center justify-center rounded-full">
          <UserIcon className="text-muted-foreground h-4 w-4" />
        </div>
        <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Sair">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
  );
}
